import React, { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import {
    BOX_TYPES, MARKUP_TYPES, LINE_TYPES, STICKY_TOOLS, boxFrom, bboxOf, moveGeom, resizeGeom, canResize,
    cloudPath, arrowHead, textRectsBetween, FILL_TYPES, TEXT_TYPES, fullStyle, calloutStart, BOXED_TEXT, FONT_STACKS,
} from "../../lib/annotGeom";
import { useImageUrl } from "../../lib/useImageUrl";
import { CommentPopup } from "./CommentPopup";

const NOTE_PX = 22;

function ImageShape({ projectId, c, g, W, H, st, zoom, hit, tid, op }) {
    const url = useImageUrl(projectId, c.image);
    const x = g.x * W, y = g.y * H, w = g.w * W, h = g.h * H;
    return (
        <g opacity={op} transform={`rotate(${g.rot || 0} ${x + w / 2} ${y + h / 2})`} {...tid}>
            {url ? <image href={url} x={x} y={y} width={w} height={h} preserveAspectRatio="none" pointerEvents="none" />
                : <rect x={x} y={y} width={w} height={h} fill="#33415555" pointerEvents="none" />}
            {st.width > 0 && <rect x={x} y={y} width={w} height={h} fill="none" stroke={st.color} strokeOpacity={st.strokeOpacity} strokeWidth={st.width * zoom} pointerEvents="none" />}
            <rect x={x} y={y} width={w} height={h} fill="transparent" pointerEvents="all" {...hit} />
        </g>
    );
}
const calloutGeom = (p0, p1, tiny) => (tiny
    ? { x: p0.x + 0.03, y: p0.y - 0.08, w: 0.16, h: 0.05, ax: p0.x, ay: p0.y }
    : { x: p1.x, y: p1.y - 0.025, w: 0.16, h: 0.05, ax: p0.x, ay: p0.y });
const HANDLES = { nw: [0, 0], n: [0.5, 0], ne: [1, 0], e: [1, 0.5], se: [1, 1], s: [0.5, 1], sw: [0, 1], w: [0, 0.5] };

function shapePath(type, g, W, H, zoom, sw) {
    const X = (v) => v * W, Y = (v) => v * H;
    if (type === "rect" || type === "comment") return `M ${X(g.x)} ${Y(g.y)} h ${X(g.w)} v ${Y(g.h)} h ${-X(g.w)} Z`;
    if (type === "ellipse") {
        const rx = X(g.w) / 2, ry = Y(g.h) / 2, cx = X(g.x) + rx, cy = Y(g.y) + ry;
        return `M ${cx - rx} ${cy} A ${rx} ${ry} 0 1 0 ${cx + rx} ${cy} A ${rx} ${ry} 0 1 0 ${cx - rx} ${cy} Z`;
    }
    if (type === "cloud") return cloudPath(X(g.x), Y(g.y), X(g.w), Y(g.h), 9 * zoom);
    if (LINE_TYPES.includes(type)) {
        let d = `M ${X(g.x1)} ${Y(g.y1)} L ${X(g.x2)} ${Y(g.y2)}`;
        if (type === "arrow") {
            const [, l, r] = arrowHead(X(g.x1), Y(g.y1), X(g.x2), Y(g.y2), Math.max(10 * zoom, sw * 4));
            d += ` M ${l[0]} ${l[1]} L ${X(g.x2)} ${Y(g.y2)} L ${r[0]} ${r[1]}`;
        }
        return d;
    }
    if (type === "draw") return (g.points || []).map(([x, y], i) => `${i ? "L" : "M"} ${X(x)} ${Y(y)}`).join(" ");
    return "";
}

export function CommentLayer({ W, H, zoom, page, pdfDoc, comments, tool, setTool, styleFor, selectedId, setSelectedId, cm, onReply, visible, dateFmt, t, multiIds = [], setMultiIds = () => {} }) {
    const svgRef = useRef(null);
    const opRef = useRef(null);
    const textRef = useRef({ page: 0, items: null });
    const clipRef = useRef(null);
    const [draft, setDraft] = useState(null);
    const [drag, setDrag] = useState(null);
    const [editId, setEditId] = useState(null);
    const pageComments = comments.filter((c) => c.page === page);
    const pt = (e) => { const r = svgRef.current.getBoundingClientRect(); return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height }; };
    const lastPtRef = useRef(null);
    const fileRef = useRef(null);

    const addImage = async (file, at) => {
        if (!file || !file.type?.startsWith("image/")) return;
        const name = await cm.uploadImage(file);
        if (!name) return;
        const dims = await new Promise((res) => {
            const img = new Image();
            const u = URL.createObjectURL(file);
            img.onload = () => { res([img.naturalWidth || 1, img.naturalHeight || 1]); URL.revokeObjectURL(u); };
            img.onerror = () => res([4, 3]);
            img.src = u;
        });
        const w = 0.3, h = Math.min(0.9, w * (dims[1] / dims[0]) * (W / H));
        const p = at || lastPtRef.current || { x: 0.5, y: 0.35 };
        const geom = { x: Math.max(0, Math.min(1 - w, p.x - w / 2)), y: Math.max(0, Math.min(1 - h, p.y - h / 2)), w, h, rot: 0 };
        create("image", geom, { image: name });
    };

    useEffect(() => {
        if (tool !== "image") return;
        fileRef.current?.click();
        setTool(null);
    }, [tool, setTool]);

    useEffect(() => {
        if (!visible || !W) return;
        const onPaste = (e) => {
            const tag = (e.target?.tagName || "").toLowerCase();
            if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
            const item = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith("image/"));
            if (item) { e.preventDefault(); addImage(item.getAsFile()); return; }
            if (clipRef.current) { e.preventDefault(); duplicate(clipRef.current); }
        };
        const host = svgRef.current?.parentElement;
        const over = (e) => { if ([...(e.dataTransfer?.types || [])].includes("Files")) e.preventDefault(); };
        const drop = (e) => {
            const f = [...(e.dataTransfer?.files || [])].find((x) => x.type.startsWith("image/"));
            if (!f) return;
            e.preventDefault();
            addImage(f, pt(e));
        };
        window.addEventListener("paste", onPaste);
        host?.addEventListener("dragover", over);
        host?.addEventListener("drop", drop);
        return () => { window.removeEventListener("paste", onPaste); host?.removeEventListener("dragover", over); host?.removeEventListener("drop", drop); };
    });

    useEffect(() => {
        if (!pdfDoc || !MARKUP_TYPES.includes(tool) || textRef.current.page === page) return;
        let off = false;
        (async () => {
            const pg = await pdfDoc.getPage(page);
            const vp = pg.getViewport({ scale: 1 });
            const tc = await pg.getTextContent();
            if (off) return;
            const items = tc.items.filter((i) => i.str && i.str.trim()).map((i) => {
                const tx = pdfjsLib.Util.transform(vp.transform, i.transform);
                const fh = Math.hypot(tx[2], tx[3]) || 1;
                return { x: tx[4] / vp.width, y: (tx[5] - fh) / vp.height, w: i.width / vp.width, h: (fh * 1.15) / vp.height, n: i.str.length };
            });
            textRef.current = { page, items };
        })();
        return () => { off = true; };
    }, [pdfDoc, page, tool]);

    const create = async (type, geom, extra = {}) => {
        const c = await cm.create({ page, type, geom, style: styleFor(type), text: "", ...extra });
        if (c) { setSelectedId(c.id); if (["note", "comment", "callout", "text"].includes(type)) setEditId(c.id); }
        return c;
    };

    const finishDraft = () => {
        const d = draft;
        setDraft(null);
        if (!d) return;
        const { type, p0, p1 } = d;
        const box = boxFrom(p0, p1);
        const tiny = box.w < 0.006 && box.h < 0.006;
        let geom = null;
        if (type === "note") geom = { x: p0.x, y: p0.y };
        else if (type === "text") geom = tiny ? { x: p0.x, y: p0.y, w: 0.2, h: 0.04 } : box;
        else if (type === "comment") geom = tiny ? { x: p0.x, y: p0.y, w: 0.16, h: 0.05 } : box;
        else if (type === "callout") geom = calloutGeom(p0, p1, tiny);
        else if (LINE_TYPES.includes(type)) geom = tiny ? null : { x1: p0.x, y1: p0.y, x2: p1.x, y2: p1.y };
        else if (BOX_TYPES.includes(type)) geom = tiny ? null : box;
        else if (type === "draw") geom = d.points.length > 1 ? { points: d.points } : null;
        else {
            const rects = textRectsBetween(textRef.current.page === page ? textRef.current.items : null, p0, p1);
            geom = rects ? { rects } : tiny ? null : { rects: [box] };
        }
        if (geom) create(type, geom);
        if (!STICKY_TOOLS.includes(type)) setTool(null);
    };

    useEffect(() => {
        const move = (e) => {
            if (svgRef.current) {
                const p0 = pt(e);
                if (p0.x >= 0 && p0.x <= 1 && p0.y >= 0 && p0.y <= 1) lastPtRef.current = p0;
            }
            const op = opRef.current;
            if (!op) return;
            const p = pt(e);
            if (op.kind === "draw") {
                setDraft((d) => {
                    if (!d) return d;
                    const last = d.points[d.points.length - 1];
                    const add = d.type === "draw" && Math.hypot(p.x - last[0], p.y - last[1]) > 0.002;
                    return { ...d, p1: p, points: add ? [...d.points, [p.x, p.y]] : d.points };
                });
                return;
            }
            if (op.kind === "rotate") {
                const g0 = op.startGeom;
                let a = (Math.atan2((p.y - (g0.y + g0.h / 2)) * H, (p.x - (g0.x + g0.w / 2)) * W) * 180) / Math.PI + 90;
                if (e.shiftKey) a = Math.round(a / 15) * 15;
                op.moved = true;
                op.geom = { ...g0, rot: Math.round(((a % 360) + 360) % 360) };
                setDrag({ id: op.id, geom: op.geom });
                return;
            }
            const dx = p.x - op.start.x, dy = p.y - op.start.y;
            if (Math.abs(dx) + Math.abs(dy) > 0.002) op.moved = true;
            op.geom = op.kind === "move" ? moveGeom(op.type, op.startGeom, dx, dy) : resizeGeom(op.type, op.startGeom, op.handle, dx, dy);
            setDrag({ id: op.id, geom: op.geom });
        };
        const up = () => {
            const op = opRef.current;
            opRef.current = null;
            if (!op) return;
            if (op.kind === "draw") { finishDraft(); return; }
            if (op.moved && op.geom) cm.update(op.id, { geom: op.geom });
            setDrag(null);
        };
        window.addEventListener("mousemove", move);
        window.addEventListener("mouseup", up);
        return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    });

    const duplicate = (c) => create(c.type, moveGeom(c.type, c.geom, 0.02, 0.02), { style: c.style, text: c.text, image: c.image });

    useEffect(() => {
        const onKey = (e) => {
            const tag = (e.target?.tagName || "").toLowerCase();
            if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
            const sel = pageComments.find((c) => c.id === selectedId);
            const k = (e.key || "").toLowerCase();
            if (e.key === "Escape") { setTool(null); setSelectedId(null); return; }
            if (sel && (e.key === "Delete" || e.key === "Backspace") && cm.canEdit(sel)) {
                e.preventDefault();
                pageComments.filter((c) => (c.id === sel.id || multiIds.includes(c.id)) && cm.canEdit(c)).forEach((c) => cm.remove(c.id));
                setSelectedId(null); setMultiIds([]);
                return;
            }
            if ((e.ctrlKey || e.metaKey) && k === "c" && sel) { clipRef.current = sel; }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    });

    if (!W || !visible) return null;

    const startDraw = (e) => {
        if (e.button !== 0) return;
        e.preventDefault(); e.stopPropagation();
        const p = pt(e);
        setSelectedId(null);
        opRef.current = { kind: "draw" };
        setDraft({ type: tool, p0: p, p1: p, points: [[p.x, p.y]] });
    };
    const startOp = (e, c, kind, handle) => {
        if (e.button !== 0) return;
        e.preventDefault(); e.stopPropagation();
        if (e.shiftKey && kind === "move") {
            setMultiIds((prev) => {
                const base = prev.length ? prev : selectedId ? [selectedId] : [];
                return base.includes(c.id) ? base.filter((x) => x !== c.id) : [...base, c.id];
            });
            if (!selectedId) setSelectedId(c.id);
            return;
        }
        if (kind === "move" && !multiIds.includes(c.id)) setMultiIds([]);
        setSelectedId(c.id);
        if (!cm.canEdit(c)) return;
        opRef.current = { kind, handle, id: c.id, type: c.type, start: pt(e), startGeom: c.geom, moved: false };
    };

    const renderShape = (c, g, key, interactive = true) => {
        const st = fullStyle(c);
        const color = st.color;
        const sw = (st.width || 2) * zoom;
        const hit = interactive ? { onMouseDown: (e) => startOp(e, c, "move"), style: { cursor: cm.canEdit(c) ? "move" : "pointer" } } : {};
        const tid = interactive ? { "data-testid": `annot-${c.type}-${c.id}` } : {};
        const op = c.resolved ? 0.45 : 1;
        if (MARKUP_TYPES.includes(c.type)) {
            return (
                <g key={key} opacity={op} {...tid}>
                    {(g.rects || []).map((r, i) => {
                        const x = r.x * W, y = r.y * H, w = r.w * W, h = r.h * H;
                        const lw = Math.max(1.2, h * 0.09);
                        return (
                            <g key={i}>
                                {c.type === "highlight" && <rect x={x} y={y} width={w} height={h} fill={color} opacity={0.38} style={{ mixBlendMode: "multiply" }} pointerEvents="none" />}
                                {c.type === "underline" && <line x1={x} y1={y + h * 0.95} x2={x + w} y2={y + h * 0.95} stroke={color} strokeWidth={lw} pointerEvents="none" />}
                                {c.type === "strike" && <line x1={x} y1={y + h / 2} x2={x + w} y2={y + h / 2} stroke={color} strokeWidth={lw} pointerEvents="none" />}
                                <rect x={x} y={y} width={w} height={h} fill="transparent" pointerEvents="all" {...hit} />
                            </g>
                        );
                    })}
                </g>
            );
        }
        if (c.type === "image") return <ImageShape key={key} projectId={cm.projectId} c={c} g={g} W={W} H={H} st={st} zoom={zoom} hit={hit} tid={tid} op={op} />;
        if (c.type === "note") {
            const x = g.x * W, y = g.y * H, s = NOTE_PX;
            return (
                <g key={key} opacity={op} transform={`translate(${x} ${y})`} {...tid} {...hit} pointerEvents="all">
                    <path d={`M0 0 H${s} V${s * 0.7} L${s * 0.7} ${s} H0 Z`} fill={st.fill || "#f59e0b"} fillOpacity={st.fillOpacity} stroke={color} strokeOpacity={st.strokeOpacity} strokeWidth={1} />
                    <path d={`M${s} ${s * 0.7} H${s * 0.7} V${s}`} fill="#00000033" stroke={color} strokeOpacity={st.strokeOpacity} strokeWidth={1} />
                    <path d={`M4 6 H${s - 4} M4 10 H${s - 4} M4 14 H${s * 0.6}`} stroke={st.textColor} strokeWidth={1.2} />
                </g>
            );
        }
        const isCallout = c.type === "callout";
        const d = shapePath(isCallout ? "rect" : c.type, g, W, H, zoom, sw);
        const hasFill = FILL_TYPES.includes(c.type) && st.fill && st.fillOpacity > 0;
        const boxed = BOXED_TEXT.includes(c.type) || isCallout;
        let leader = "";
        if (isCallout) {
            const s0 = calloutStart(g);
            const [, l, r] = arrowHead(s0.x * W, s0.y * H, g.ax * W, g.ay * H, Math.max(9 * zoom, sw * 3.5));
            leader = `M ${s0.x * W} ${s0.y * H} L ${g.ax * W} ${g.ay * H} M ${l[0]} ${l[1]} L ${g.ax * W} ${g.ay * H} L ${r[0]} ${r[1]}`;
        }
        const showText = TEXT_TYPES.includes(c.type) && c.text;
        return (
            <g key={key} opacity={op} {...tid}>
                {hasFill && <path d={d} fill={st.fill} fillOpacity={st.fillOpacity} pointerEvents="none" />}
                {(boxed || hasFill) && <path d={d} fill="transparent" pointerEvents="all" {...hit} />}
                <path d={d} fill="none" stroke={color} strokeOpacity={st.strokeOpacity} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" pointerEvents="none" />
                {isCallout && <path d={leader} fill="none" stroke={color} strokeOpacity={st.strokeOpacity} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" pointerEvents="none" />}
                {isCallout && <path d={leader} fill="none" stroke="transparent" strokeWidth={Math.max(12, sw + 8)} pointerEvents="stroke" {...hit} />}
                {showText && (
                    <foreignObject x={g.x * W} y={g.y * H} width={g.w * W} height={g.h * H} pointerEvents="none">
                        <div style={{
                            fontSize: (st.fontSize || 12) * zoom, color: st.textColor, padding: 4 * zoom, lineHeight: 1.25, whiteSpace: "pre-wrap", overflow: "hidden", height: "100%",
                            fontFamily: FONT_STACKS[st.fontFamily] || FONT_STACKS.sans, fontWeight: st.bold ? 700 : 400, fontStyle: st.italic ? "italic" : "normal",
                            textDecoration: st.underline ? "underline" : "none", wordBreak: "break-word", display: "flex", flexDirection: "column",
                            justifyContent: boxed ? "flex-start" : "center", textAlign: st.align || (boxed ? "left" : "center"),
                        }}>{c.text}</div>
                    </foreignObject>
                )}
                {!boxed && <path d={d} fill="none" stroke="transparent" strokeWidth={Math.max(12, sw + 8)} pointerEvents="stroke" {...hit} />}
            </g>
        );
    };

    const sel = pageComments.find((c) => c.id === selectedId);
    const selGeom = sel ? (drag?.id === sel.id ? drag.geom : sel.geom) : null;
    const selBox = sel ? bboxOf({ type: sel.type, geom: selGeom }, NOTE_PX / W, NOTE_PX / H) : null;

    const renderSelection = () => {
        if (!sel) return null;
        const b = { x: selBox.x * W, y: selBox.y * H, w: selBox.w * W, h: selBox.h * H };
        const editable = cm.canEdit(sel) && canResize(sel.type) && !drag;
        const hs = 8;
        const handle = (key, cx, cy) => (
            <rect key={key} data-testid={`annot-handle-${key}`} x={cx - hs / 2} y={cy - hs / 2} width={hs} height={hs} fill="#fff" stroke="#2563eb" strokeWidth={1.5}
                style={{ cursor: key.length === 2 && key[0] !== "p" ? `${key}-resize` : key === "n" || key === "s" ? "ns-resize" : key === "e" || key === "w" ? "ew-resize" : "crosshair" }}
                pointerEvents="all" onMouseDown={(e) => startOp(e, sel, "resize", key)} />
        );
        return (
            <g>
                <rect x={b.x - 4} y={b.y - 4} width={b.w + 8} height={b.h + 8} fill="none" stroke="#2563eb" strokeWidth={1} strokeDasharray="4 3" pointerEvents="none" />
                {editable && LINE_TYPES.includes(sel.type) && [
                    handle("p1", selGeom.x1 * W, selGeom.y1 * H), handle("p2", selGeom.x2 * W, selGeom.y2 * H),
                ]}
                {editable && sel.type === "callout" && [
                    ...Object.entries(HANDLES).map(([k, [fx, fy]]) => handle(k, (selGeom.x + fx * selGeom.w) * W, (selGeom.y + fy * selGeom.h) * H)),
                    handle("a", selGeom.ax * W, selGeom.ay * H),
                ]}
                {editable && !LINE_TYPES.includes(sel.type) && sel.type !== "callout" && Object.entries(HANDLES).map(([k, [fx, fy]]) => handle(k, b.x + fx * b.w, b.y + fy * b.h))}
                {editable && sel.type === "image" && (
                    <g>
                        <line x1={b.x + b.w / 2} y1={b.y} x2={b.x + b.w / 2} y2={b.y - 22} stroke="#2563eb" strokeWidth={1} pointerEvents="none" />
                        <circle data-testid="annot-handle-rotate" cx={b.x + b.w / 2} cy={b.y - 26} r={6} fill="#fff" stroke="#2563eb" strokeWidth={1.5}
                            style={{ cursor: "grab" }} pointerEvents="all" onMouseDown={(e) => startOp(e, sel, "rotate")} />
                    </g>
                )}
            </g>
        );
    };
    const renderMulti = () => pageComments.filter((c) => multiIds.includes(c.id) && c.id !== selectedId).map((c) => {
        const b = bboxOf(c, NOTE_PX / W, NOTE_PX / H);
        return <rect key={`m-${c.id}`} data-testid={`annot-multi-${c.id}`} x={b.x * W - 4} y={b.y * H - 4} width={b.w * W + 8} height={b.h * H + 8} fill="none" stroke="#f59e0b" strokeWidth={1} strokeDasharray="4 3" pointerEvents="none" />;
    });

    const draftShape = () => {
        if (!draft) return null;
        const { type, p0, p1 } = draft;
        const fake = { id: "draft", type, style: styleFor(type), text: "" };
        if (type === "note") return null;
        if (type === "callout") return renderShape(fake, calloutGeom(p0, p1, false), "draft", false);
        if (BOX_TYPES.includes(type)) return renderShape(fake, boxFrom(p0, p1), "draft", false);
        if (LINE_TYPES.includes(type)) return renderShape(fake, { x1: p0.x, y1: p0.y, x2: p1.x, y2: p1.y }, "draft", false);
        if (type === "draw") return renderShape(fake, { points: draft.points }, "draft", false);
        const rects = textRectsBetween(textRef.current.page === page ? textRef.current.items : null, p0, p1);
        if (rects) return renderShape(fake, { rects }, "draft", false);
        const b = boxFrom(p0, p1);
        return <rect x={b.x * W} y={b.y * H} width={b.w * W} height={b.h * H} fill="none" stroke={fake.style.color} strokeDasharray="4 3" />;
    };

    const popupLeft = selBox ? Math.min(W - 270, (selBox.x + selBox.w) * W + 12) : 0;
    const popupTop = selBox ? Math.max(0, selBox.y * H) : 0;

    return (
        <>
            <svg ref={svgRef} data-testid="comment-layer" width={W} height={H} className="absolute inset-0"
                style={{ zIndex: tool ? 60 : 35, pointerEvents: "none", cursor: tool ? "crosshair" : "default" }}>
                {[...pageComments].sort((a, b) => (b.type === "image") - (a.type === "image")).map((c) => renderShape(c, drag?.id === c.id ? drag.geom : c.geom, c.id))}
                {renderMulti()}
                {renderSelection()}
                {draftShape()}
                {tool && <rect data-testid="comment-draw-surface" x={0} y={0} width={W} height={H} fill="transparent" pointerEvents="all" onMouseDown={startDraw} />}
            </svg>
            <input ref={fileRef} data-testid="comment-image-input" type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; addImage(f); }} />
            {sel && !drag && !tool && (
                <CommentPopup c={sel} left={Math.max(0, popupLeft)} top={popupTop} cm={cm} t={t} dateFmt={dateFmt}
                    autoFocus={editId === sel.id} onEdited={() => setEditId(null)}
                    onCopy={() => duplicate(sel)} onReply={() => onReply(sel)}
                    onDelete={() => { cm.remove(sel.id); setSelectedId(null); }}
                    onClose={() => setSelectedId(null)} />
            )}
        </>
    );
}
